import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import client from "../api/client";

function AccountPage() {
  const { user, logout } = useAuth();
  const { settings, updateSettings } = useSettings();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [displayNameSaved, setDisplayNameSaved] = useState(false);
  const [displayNameError, setDisplayNameError] = useState("");

  useEffect(() => {
    client.get("/account")
      .then(res => setDisplayName(res.data.display_name || ""))
      .catch(() => {});
  }, []);

  const handleToggle = async (key) => {
    await updateSettings({ [key]: !settings[key] });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleDisplayNameSave = async () => {
    setDisplayNameError("");
    try {
      await client.put("/account", { display_name: displayName });
      setDisplayNameSaved(true);
      setTimeout(() => setDisplayNameSaved(false), 2000);
    } catch (err) {
      setDisplayNameError("Failed to save. Please try again.");
    }
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    setError("");
    setIsDeleting(true);
    try {
      await client.delete("/account", { data: { password } });
      navigate("/login", { state: { message: "Your account has been deleted." } });
      await logout();
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Incorrect password. Please try again.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleStyle = (active) => ({
    width: "40px",
    height: "22px",
    borderRadius: "11px",
    background: active ? "rgba(125, 211, 232, 0.4)" : "rgba(255,255,255,0.1)",
    border: active ? "1px solid rgba(125, 211, 232, 0.6)" : "1px solid rgba(255,255,255,0.15)",
    position: "relative",
    cursor: "pointer",
    transition: "background 0.2s, border 0.2s",
    flexShrink: 0,
  });

  const knobStyle = (active) => ({
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    background: active ? "#7dd3e8" : "rgba(255,255,255,0.3)",
    position: "absolute",
    top: "2px",
    left: active ? "20px" : "2px",
    transition: "left 0.2s, background 0.2s",
  });

  const Toggle = ({ settingKey, label, description }) => (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "14px 0",
      borderBottom: "1px solid rgba(255,255,255,0.06)"
    }}>
      <div>
        <div style={{ fontSize: "14px", fontWeight: "500", marginBottom: "3px" }}>{label}</div>
        <div style={{ fontSize: "12px", opacity: 0.5 }}>{description}</div>
      </div>
      <div style={toggleStyle(settings[settingKey])} onClick={() => handleToggle(settingKey)}>
        <div style={knobStyle(settings[settingKey])} />
      </div>
    </div>
  );

  return (
    <main style={{ maxWidth: "520px", margin: "0 auto" }}>
      <h1>Account</h1>

      {/* ACCOUNT INFO */}
      <div style={{
        background: "rgba(255,255,255,0.05)",
        borderRadius: "10px",
        padding: "16px 20px",
        marginBottom: "24px",
        fontSize: "14px"
      }}>
        <div style={{ opacity: 0.5, fontSize: "12px", marginBottom: "4px", letterSpacing: "1px", textTransform: "uppercase" }}>
          Logged in as
        </div>
        <div style={{ fontWeight: "500" }}>{user?.email}</div>
      </div>

      {/* DISPLAY NAME */}
      <div style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: "10px",
        padding: "16px 20px",
        marginBottom: "24px",
      }}>
        <h2 style={{
          fontSize: "12px", letterSpacing: "1.5px", textTransform: "uppercase",
          color: "#7dd3e8", margin: "0 0 4px 0", opacity: 0.85
        }}>
          Display Name
        </h2>
        <p style={{ fontSize: "12px", opacity: 0.45, marginBottom: "12px", marginTop: "4px" }}>
          Used on the site to identify you in your entries and session flows. This is optional and can be changed at any time.
        </p>
        <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
          <label style={{ flex: 1, marginBottom: 0 }}>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Stephanie"
              style={{ marginBottom: 0 }}
            />
          </label>
          <button
            onClick={handleDisplayNameSave}
            style={{ padding: "10px 18px", fontSize: "13px", whiteSpace: "nowrap" }}
          >
            Save
          </button>
        </div>
        {displayNameSaved && (
          <div style={{ fontSize: "12px", color: "#7dd3e8", opacity: 0.8, marginTop: "8px" }}>
            Saved
          </div>
        )}
        {displayNameError && (
          <p className="error" style={{ marginTop: "8px" }}>{displayNameError}</p>
        )}
      </div>

      {/* DISPLAY PREFERENCES */}
      <div style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: "10px",
        padding: "16px 20px",
        marginBottom: "24px",
      }}>
        <h2 style={{
          fontSize: "12px", letterSpacing: "1.5px", textTransform: "uppercase",
          color: "#7dd3e8", margin: "0 0 4px 0", opacity: 0.85
        }}>
          Display Preferences
        </h2>
        <p style={{ fontSize: "12px", opacity: 0.45, marginBottom: "12px", marginTop: "4px" }}>
          Choose which sections appear in your entries.
        </p>

        <Toggle
          settingKey="show_celestial"
          label="Celestial Context"
          description="Moon phase and season on entry detail"
        />
        <Toggle
          settingKey="show_mind_body"
          label="Mind & Body"
          description="Chakra check-in, somatic body map, and nervous system tracking"
        />
        <Toggle
          settingKey="show_prompt"
          label="Journaling Prompt"
          description="Prompt suggestions when creating entries"
        />

        {saved && (
          <div style={{ fontSize: "12px", color: "#7dd3e8", opacity: 0.8, marginTop: "12px", textAlign: "right" }}>
            Saved
          </div>
        )}
      </div>


      {/* SESSION FLOW PREFERENCES */}
      <div style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: "10px",
        padding: "16px 20px",
        marginBottom: "24px",
      }}>
      <h2 style={{
        fontSize: "12px", letterSpacing: "1.5px", textTransform: "uppercase",
        color: "#7dd3e8", margin: "0 0 4px 0", opacity: 0.85
      }}>
        Session Flow
      </h2>
      <p style={{ fontSize: "12px", opacity: 0.45, marginBottom: "12px", marginTop: "4px" }}>
        Options for how your session flow data is stored.
      </p>
      <Toggle
        settingKey="preserve_chat_history"
        label="Preserve Chat History"
        description="Save session chat transcripts and make them viewable on your personal journal entries"
      />
      {saved && (
        <div style={{ fontSize: "12px", color: "#7dd3e8", opacity: 0.8, marginTop: "12px", textAlign: "right" }}>
          Saved
        </div>
      )}
    </div>

      {/* DANGER ZONE */}
      <div style={{
        border: "1px solid rgba(255, 100, 100, 0.25)",
        borderRadius: "10px",
        padding: "20px",
      }}>
        <h2 style={{ fontSize: "14px", color: "#ff9a9a", marginTop: 0, marginBottom: "8px", letterSpacing: "1px", textTransform: "uppercase" }}>
          Danger Zone
        </h2>
        <p style={{ fontSize: "14px", opacity: 0.9, marginBottom: "20px", lineHeight: "1.6" }}>
          Deleting your account is permanent and cannot be undone. All your journal entries and data will be erased.
        </p>

        <form onSubmit={handleDelete}>
          <label>
            Enter your password to confirm
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              required
            />
          </label>

          {error && <p className="error">{error}</p>}

          <button
            type="submit"
            disabled={isDeleting || !password}
            style={{
              marginTop: "8px",
              background: "rgba(255, 100, 100, 0.15)",
              border: "1px solid rgba(255, 100, 100, 0.4)",
              color: "#ff9a9a",
            }}
          >
            {isDeleting ? "Deleting..." : "Delete My Account"}
          </button>
        </form>
      </div>
    </main>
  );
}

export default AccountPage;